// Library imports
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

// User Component imports
import { PageContent, PageHeader, PageWrapper, PageContainer } from 'components/common/layout_element/LayoutElement';
import { Row, Button, TextInput, FlexibleTextInput, Stack, Card, Text, Uploader, Select, Icon, CommonModal } from "_core/components/common/UI";
import * as Constants from "_core/configs/Constants";
import { FormatDateTime } from "_core/utils/common";
import { useUpdateLock } from "_core/hooks/useUpdateLock";
import { TextSwitch } from "components/common/parts";
import { setLoading, setErrorModal, setMenuState, setInitializeState } from "_core/slices/scene/SceneSlice";
import AdminInquirySettingApi from "apis/AdminInquirySettingApi";
import AdminInquirySettingUpdateApi from "apis/AdminInquirySettingUpdateApi";
import AdminDownloadTemplateApi from "apis/AdminDownloadTemplateApi";
import AdminDownloadFileApi from "apis/AdminDownloadFileApi";

import './AdminUserInquirySetting.scss';

const FORM_ITEM_TYPES = {
    "text": { label: "テキスト (1行)", defaultPlaceholder: "" },
    "textarea": { label: "テキスト (複数行)", defaultPlaceholder: "" },
    "email": { label: "メールアドレス", defaultPlaceholder: "" },
    "tel": { label: "電話番号", defaultPlaceholder: "" },
    "alphanumeric": { label: "英数のみ", defaultPlaceholder: "" }
};

const getTypeOptions = () => {
    const options = {};
    Object.keys(FORM_ITEM_TYPES).forEach(k => options[FORM_ITEM_TYPES[k].label] = k);
    return options;
};

const AdminUserInquirySetting = () => {
    const dispatch = useDispatch();

    // ページ最上部への参照: スクロールトップボタンで使用
    const topRef = useRef(null);
    const scrollToRef = (targetRef) => {
        if (targetRef.current) {
            targetRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    const CallAdminInquirySettingApi = AdminInquirySettingApi();
    const CallAdminInquirySettingUpdateApi = AdminInquirySettingUpdateApi();
    const CallAdminDownloadTemplateApi = AdminDownloadTemplateApi();
    const CallAdminDownloadFileApi = AdminDownloadFileApi();

    const [mailAddress, setMailAddress] = useState("");
    const [mailSubject, setMailSubject] = useState("");
    const [formDescription, setFormDescription] = useState("");
    const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState("");
    const [thanksPageMode, setThanksPageMode] = useState("popup");
    const [thanksHeading, setThanksHeading] = useState("");
    const [thanksText, setThanksText] = useState("");
    const [thanksUrl, setThanksUrl] = useState("");
    const [formItems, setFormItems] = useState([]);
    const [isEnabled, setIsEnabled] = useState(false);
    const [isWebhookEnabled, setIsWebhookEnabled] = useState(false);
    const [webhookUrl, setWebhookUrl] = useState("");
    const [buttonLabel, setButtonLabel] = useState("有人引き継ぎ");

    const [newItemRequired, setNewItemRequired] = useState("true");
    const [newItemName, setNewItemName] = useState("");
    const [newItemLabel, setNewItemLabel] = useState("");
    const [newItemType, setNewItemType] = useState("text");
    const [newItemPlaceholder, setNewItemPlaceholder] = useState(FORM_ITEM_TYPES["text"].defaultPlaceholder);
    const [resetKey, setResetKey] = useState(0);

    const [isFormError, setIsFormError] = useState(false);

    // ドラッグ&ドロップによる並び替え用のstate
    const [dragItemIndex, setDragItemIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    // ダブルクリック編集用のstate (行をダブルクリックで編集モードへ)
    const [editingIndex, setEditingIndex] = useState(null);
    const [editingRequired, setEditingRequired] = useState("true");
    const [editingName, setEditingName] = useState("");
    const [editingLabel, setEditingLabel] = useState("");
    const [editingType, setEditingType] = useState("text");
    const [editingPlaceholder, setEditingPlaceholder] = useState("");

    const startEditItem = (index) => {
        const item = formItems[index];
        if (!item) return;
        setEditingIndex(index);
        setEditingRequired(item.required ? "true" : "false");
        setEditingName(item.name || "");
        setEditingLabel(item.label || "");
        setEditingType(item.type || "text");
        setEditingPlaceholder(item.placeholder || "");
    };

    const cancelEditItem = () => {
        setEditingIndex(null);
    };

    const saveEditItem = () => {
        if (editingIndex === null) return;
        if (!/^[A-Za-z]*$/.test(editingName)) {
            setModalInfo({ show: true, title: "確認", text: "管理名にはアルファベットのみを入力してください。" });
            return;
        }
        if (!editingLabel) {
            setModalInfo({ show: true, title: "確認", text: "見出しを入力してください。" });
            return;
        }
        const newItems = [...formItems];
        newItems[editingIndex] = {
            required: editingRequired === "true",
            name: editingName,
            label: editingLabel,
            type: editingType,
            placeholder: editingPlaceholder,
        };
        setFormItems(newItems);
        setEditingIndex(null);
    };

    const handleTypeChange = (val) => {
        setNewItemType(val);
        setNewItemPlaceholder(FORM_ITEM_TYPES[val]?.defaultPlaceholder || "");
    };
    const [summaryFormatAsset, setSummaryFormatAsset] = useState([]);
    const [summaryFormatTAsset, setSummaryFormatTAsset] = useState(null);

    const [isLoaded, setIsLoaded] = useState(false);

    // 編集前の値 (差分検出用)
    const [initialValues, setInitialValues] = useState({
        mail_address: "",
        mail_subject: "",
        form_description: "",
        privacy_policy_url: "",
        thanks_heading: "",
        thanks_text: "",
        thanks_url: "",
        form_items: "[]",
        is_enabled: false,
        is_webhook_enabled: false,
        webhook_url: "",
        button_label: "有人引き継ぎ",
    });

    // モーダル
    const [modalInfo, setModalInfo] = useState({
        show: false,
        text: '',
        onClick: null,
    });

    const { tAssetList } = useSelector((store) => store.transaction);
    const { initializeState } = useSelector((store) => store.scene);
    // デモユーザーと読み取り専用権限は、どちらも更新系を使わせないためまとめて扱う
    // このページはフォーム項目の追加・編集・削除もローカルstate操作なので、入力欄ごと非活性にする
    const { isUpdateLocked } = useUpdateLock();

    useEffect(() => {
        dispatch(setMenuState({ isHeader: false, isMenu: true, isFooter: false, isFullScreen: false }));
    }, [dispatch]);

    useEffect(() => {
        if (initializeState !== Constants.INITIALIZE_STATE.allComplete) {
            return;
        }
        dispatch(setInitializeState(Constants.INITIALIZE_STATE.pageDataFetching));
        loadData();
    }, [initializeState]);

    const loadData = async () => {
        dispatch(setLoading(true));
        const { response } = await CallAdminInquirySettingApi({});
        if (response && response.error_code === 0) {
            const data = response.t_inquiry_setting;
            const loaded = {
                mail_address: (data && data.mail_address) || "",
                mail_subject: (data && data.mail_subject) || "",
                form_description: (data && data.form_description) || "",
                privacy_policy_url: (data && data.privacy_policy_url) || "",
                thanks_heading: (data && data.thanks_heading) || "",
                thanks_text: (data && data.thanks_text) || "",
                thanks_url: (data && data.thanks_url) || "",
                form_items: (data && data.form_items) || "[]",
                is_enabled: !!(data && data.is_enabled),
                is_webhook_enabled: !!(data && data.is_webhook_enabled),
                webhook_url: (data && data.webhook_url) || "",
                button_label: (data && data.button_label) || "有人引き継ぎ",
            };

            setMailAddress(loaded.mail_address);
            setMailSubject(loaded.mail_subject);
            setFormDescription(loaded.form_description);
            setPrivacyPolicyUrl(loaded.privacy_policy_url);
            setThanksHeading(loaded.thanks_heading);
            setThanksText(loaded.thanks_text);
            setThanksUrl(loaded.thanks_url);
            setIsEnabled(loaded.is_enabled);
            setIsWebhookEnabled(loaded.is_webhook_enabled);
            setWebhookUrl(loaded.webhook_url);
            setButtonLabel(loaded.button_label);
            setThanksPageMode(loaded.thanks_url === "" ? "popup" : "newPage");

            let parsedFormItems = [];
            try {
                parsedFormItems = JSON.parse(loaded.form_items);
            } catch (e) {
                console.error("Failed to parse form items", e);
            }
            setFormItems(parsedFormItems);

            if (data && data.summary_format_t_asset_id && tAssetList) {
                const tAsset = tAssetList.find(a => a.id === data.summary_format_t_asset_id);
                if (tAsset) {
                    setSummaryFormatTAsset(tAsset);
                }
            }

            setInitialValues(loaded);
            setIsLoaded(true);
        } else {
            dispatch(setErrorModal({ show: true, text: response.error_detail }));
        }
        dispatch(setLoading(false));
        dispatch(setInitializeState(Constants.INITIALIZE_STATE.pageDataFetchComplete));
    };

    // 編集中の差分を検出
    const hasChanges = () => {
        if (!isLoaded) return false;
        if (mailAddress !== initialValues.mail_address) return true;
        if (mailSubject !== initialValues.mail_subject) return true;
        if (formDescription !== initialValues.form_description) return true;
        if (privacyPolicyUrl !== initialValues.privacy_policy_url) return true;
        if (thanksPageMode === "popup") {
            if (thanksHeading !== initialValues.thanks_heading) return true;
            if (thanksText !== initialValues.thanks_text) return true;
        }
        if (thanksPageMode === "newPage") {
            if (thanksUrl !== initialValues.thanks_url) return true;
        }
        if (JSON.stringify(formItems) !== initialValues.form_items) return true;
        if (isEnabled !== initialValues.is_enabled) return true;
        if (isWebhookEnabled !== initialValues.is_webhook_enabled) return true;
        if (webhookUrl !== initialValues.webhook_url) return true;
        if (buttonLabel !== initialValues.button_label) return true;
        if (summaryFormatAsset && summaryFormatAsset.length > 0 && summaryFormatAsset[0].file instanceof File) return true;
        return false;
    };

    // 有人引き継ぎ機能を ON にするためにバックエンドが要求する条件と同じものをフロントでもガード
    const canEnableInquiry = () => {
        if (thanksPageMode === "popup") {
            if (!isThanksHeadingValid()) return false;
            if (!isThanksTextValid()) return false;
        }
        else {
            if (!isThanksUrlValid()) return false;
        }

        return isMailAddressValid()
            && isMailSubjectValid()
            && Array.isArray(formItems) && formItems.length > 0;
    };

    const handleToggleEnabled = (next) => {
        if (next && !canEnableInquiry()) {
            setModalInfo({
                show: true,
                title: "有効化できません",
                text: "有人引き継ぎを有効化するには、必須項目とフォーム項目を1件以上設定してください。",
            });
            return;
        }
        setIsEnabled(next);
    };

    const handleWebhookToggleEnabled = (enabled) => {
        if (enabled && webhookUrl === "") {
            setModalInfo({
                show: true,
                title: "有効化できません",
                text: "Webhookを有効化するには、URLを設定してください。",
            });
            return;
        }
        setIsWebhookEnabled(enabled);
    };

    // フォームのバリデーション
    const checkFormItem = () => {
        // Webhookを使用しない場合はスキップ
        if (!isWebhookEnabled) {
            return true;
        }

        // 管理名が設定されているか確認する
        for (const formItem of formItems) {
            if (!("name" in formItem) || formItem.name === "") {
                return false;
            }
        }

        return true;
    }

    // 各必須項目のバリデーション (値ベース)
    const isMailAddressValid = () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mailAddress || "");
    const isMailSubjectValid = () => (mailSubject || "").length > 0;
    const isThanksHeadingValid = () => (thanksHeading || "").length > 0;
    const isThanksTextValid = () => (thanksText || "").length > 0;
    const isThanksUrlValid = () => (thanksUrl || "").length > 0;
    const isWebhookUrlValid = () => (webhookUrl || "").length > 0;

    // 更新ボタンの活性条件
    const canUpdate = () => {
        if (isUpdateLocked) return false;
        if (!isLoaded) return false;
        if (!isMailAddressValid()) return false;
        if (!isMailSubjectValid()) return false;
        if (thanksPageMode === "popup") {
            if (!isThanksHeadingValid()) return false;
            if (!isThanksTextValid()) return false;
        }
        else {
            if (!isThanksUrlValid()) return false;
        }
        if (isWebhookEnabled) {
            if (!isWebhookUrlValid()) return false;
        }
        return hasChanges();
    };

    const handleSave = async () => {
        const formEnabled = checkFormItem();
        console.log("formEnabled: ", formEnabled);
        setIsFormError(!formEnabled);
        if (!formEnabled) {
            setModalInfo({ show: true, title: "確認", text: "フォーム項目定義の管理名を設定してください。" });
            return;
        }

        dispatch(setLoading(true));

        const requestParams = {
            is_enabled: isEnabled ? 1 : 0,
            is_webhook_enabled: isWebhookEnabled ? 1 : 0,
            webhook_url: webhookUrl,
            button_label: (buttonLabel || "").trim() || "有人引き継ぎ",
            mail_address: mailAddress,
            mail_subject: mailSubject,
            form_description: formDescription,
            privacy_policy_url: privacyPolicyUrl,
            form_items: JSON.stringify(formItems),
        };
        if (thanksPageMode === "popup") {
            requestParams["thanks_heading"] = thanksHeading;
            requestParams["thanks_text"] = thanksText;
        }
        else {
            requestParams["thanks_url"] = thanksUrl;
        }
        const requestBinaryParams = {};

        if (summaryFormatAsset && summaryFormatAsset.length > 0 && summaryFormatAsset[0].file instanceof File) {
            requestBinaryParams["summary_format"] = {
                file: summaryFormatAsset[0].file,
                filename: summaryFormatAsset[0].file.name
            };
        }

        const { response } = await CallAdminInquirySettingUpdateApi(requestParams, requestBinaryParams);
        dispatch(setLoading(false));

        if (response && response.error_code === 0) {
            setSummaryFormatAsset([]);
            // Redux 経由ではなくレスポンスから直接アップロード済みアセットを反映 (loadData 内の tAssetList は closure 経由で古い値のため)
            if (response.t_asset_list && response.t_asset_list.length > 0) {
                setSummaryFormatTAsset(response.t_asset_list[0]);
            }
            await loadData();
        } else {
            dispatch(setErrorModal({ show: true, text: response.error_detail }));
        }
    };

    const handleAddItem = () => {
        if (!/^[A-Za-z]*$/.test(newItemName)) {
            setModalInfo({ show: true, title: "確認", text: "管理名にはアルファベットのみを入力してください。" });
            return;
        }
        if (!newItemLabel) {
            setModalInfo({ show: true, title: "確認", text: "見出しを入力してください。" });
            return;
        }
        setFormItems([...formItems, {
            required: newItemRequired === "true",
            name: newItemName,
            label: newItemLabel,
            type: newItemType,
            placeholder: newItemPlaceholder
        }]);
        setNewItemRequired("true");
        setNewItemName("");
        setNewItemLabel("");
        setNewItemType("text");
        setNewItemPlaceholder(FORM_ITEM_TYPES["text"].defaultPlaceholder);
        setResetKey(prev => prev + 1);
    };

    const handleRemoveItem = (index) => {
        const newItems = [...formItems];
        newItems.splice(index, 1);
        setFormItems(newItems);
    };

    // ドラッグ開始: ドラッグ対象のインデックスを記録
    const handleDragStart = (index) => {
        setDragItemIndex(index);
    };

    // ドラッグ中: ブラウザのデフォルト動作を抑制し、ドロップ先インデックスを記録
    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (index !== dragOverIndex) {
            setDragOverIndex(index);
        }
    };

    // ドロップ: ドラッグ元とドロップ先を入れ替えてリストを更新
    const handleDrop = (e, index) => {
        e.preventDefault();
        if (dragItemIndex === null || dragItemIndex === index) {
            setDragItemIndex(null);
            setDragOverIndex(null);
            return;
        }
        const newItems = [...formItems];
        const draggedItem = newItems.splice(dragItemIndex, 1)[0];
        newItems.splice(index, 0, draggedItem);
        setFormItems(newItems);
        setDragItemIndex(null);
        setDragOverIndex(null);
    };

    // ドラッグキャンセル/終了時のクリーンアップ
    const handleDragEnd = () => {
        setDragItemIndex(null);
        setDragOverIndex(null);
    };

    const handleDownloadTemplate = async () => {
        dispatch(setLoading(true));
        const { error } = await CallAdminDownloadTemplateApi({
            file_type: "INQUIRY_FORMAT",
            fallbackFilename: "要約フォーマットテンプレート.txt",
        });
        dispatch(setLoading(false));
        if (error) {
            dispatch(setErrorModal({ show: true, text: error }));
        }
    };

    const handleDownloadSummaryFormat = async () => {
        if (!summaryFormatTAsset || !summaryFormatTAsset.sas) {
            return;
        }

        // 通信中の表示を出す
        dispatch(setLoading(true));

        const result = await CallAdminDownloadFileApi({
            t_asset_id: summaryFormatTAsset.id,
            fallbackFilename: summaryFormatTAsset.path || "summary_format.txt",
        });

        // 通信中の表示を消す
        dispatch(setLoading(false));

        if (result.error || (result.response && result.response.error_code > 0)) {
            if (!result.response) {
                // エラーモーダル表示
                dispatch(setErrorModal({ show: true, text: {
                        "title": "エラー",
                        "message": "ダウンロードに失敗しました。"
                    }
                }));
            }
            else {
                // エラーモーダル表示
                dispatch(setErrorModal({ show: true, text: result.response.error_detail }));
            }
        }
    };

    const getTypeLabel = (type) => {
        return FORM_ITEM_TYPES[type] ? FORM_ITEM_TYPES[type].label : "テキスト形式";
    };

    return (
        <PageContent pageId="AdminUserInquirySetting">
            <div ref={topRef}></div>
            <PageHeader>
                <Row className="flex-both align-center width-full">
                    <Stack className="spacing-4px">
                        <div className="page-header-title">有人引き継ぎ設定</div>
                        <div className="page-header-detail">有人引き継ぎ時のメール送信先やフォームの項目を設定します。</div>
                    </Stack>
                    <Button
                        className={"header-update-button width-240px " + (canUpdate() ? "primary" : "primary disabled")}
                        onClick={handleSave}
                        disabled={!canUpdate()}
                    >
                        更新
                    </Button>
                </Row>
            </PageHeader>
            <PageWrapper>
                <PageContainer>
                    <Stack className="block-center spacing-32px">
                        <Card className="base-card">
                            <Stack className="spacing-32px align-center">
                                <Row className="input-row-area align-top spacing-16px">
                                    <Stack className="input-label-area stack flex-center spacing-4px">
                                        <Row className="flex-left align-center spacing-4px">
                                            <Text className="input-label input-label-text text-size-medium text-bold">有人引き継ぎ機能</Text>
                                        </Row>
                                        <Text className="input-label-hint text-size-small">OFFの場合は有人引き継ぎボタンが表示されません</Text>
                                    </Stack>
                                    <Stack className="input-form-area stack flex-center">
                                        <Row className="flex-left align-center spacing-12px">
                                            <label className={"inquiry-enable-switch " + (isEnabled ? "on" : "off")}>
                                                <input
                                                    type="checkbox"
                                                    checked={isEnabled}
                                                    disabled={isUpdateLocked}
                                                    onChange={(e) => handleToggleEnabled(e.target.checked)}
                                                />
                                                <span className="switch-track"><span className="switch-thumb" /></span>
                                            </label>
                                            <Text className={"text-size-medium text-bold " + (isEnabled ? "text-color-primary" : "text-color-weak")}>
                                                {isEnabled ? "ON" : "OFF"}
                                            </Text>
                                        </Row>
                                    </Stack>
                                </Row>
                                <hr />

                                <Row className="input-row-area align-top spacing-16px">
                                    <Stack className="input-label-area stack flex-center spacing-4px">
                                        <Row className="flex-left align-center spacing-4px">
                                            <Text className="input-label input-label-text text-size-medium text-bold">Webhook</Text>
                                        </Row>
                                        <Text className="input-label-hint text-size-small">有人引き継ぎが送信された時にWebhookを実行します<br />SlackのIncoming Webhook URLを入力するとSlackに通知します</Text>
                                    </Stack>
                                    <Stack className="input-form-area stack flex-center">
                                        <Row className="flex-left align-center spacing-12px">
                                            <label className={"inquiry-enable-switch " + (isWebhookEnabled ? "on" : "off")}>
                                                <input
                                                    type="checkbox"
                                                    checked={isWebhookEnabled}
                                                    disabled={isUpdateLocked}
                                                    onChange={(e) => handleWebhookToggleEnabled(e.target.checked)}
                                                />
                                                <span className="switch-track"><span className="switch-thumb" /></span>
                                            </label>
                                            <Text className={"webhook-switch-label text-size-medium text-bold " + (isWebhookEnabled ? "text-color-primary" : "text-color-weak")}>
                                                {isWebhookEnabled ? "ON" : "OFF"}
                                            </Text>
                                            <TextInput
                                                disabled={isUpdateLocked}
                                                className="width-100percent"
                                                default={webhookUrl}
                                                onchange={(e) => setWebhookUrl(e.target.value)}
                                                placeholder="WebhookのURL"
                                                type={"url"}
                                                isLoading={(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete) ? true : false}
                                                rules={isWebhookEnabled && [
                                                    { type: "minLength", value: 1 },
                                                ]}
                                            />
                                        </Row>
                                    </Stack>
                                </Row>
                                <hr />

                                <Row className="input-row-area align-top spacing-16px">
                                    <Stack className="input-label-area stack flex-center spacing-4px">
                                        <Row className="flex-left align-center spacing-4px">
                                            <Text className="input-label input-label-text text-size-medium text-bold">ボタン表示名</Text>
                                        </Row>
                                        <Text className="input-label-hint text-size-small">ウィジェット上に表示される有人引き継ぎボタンの文言<br />未入力の場合は「有人引き継ぎ」が使われます</Text>
                                    </Stack>
                                    <Stack className="input-form-area stack flex-center">
                                        <TextInput
                                            disabled={isUpdateLocked}
                                            className="width-100percent"
                                            default={buttonLabel}
                                            onchange={(e) => setButtonLabel(e.target.value)}
                                            placeholder="有人引き継ぎ"
                                            isLoading={(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete) ? true : false}
                                            rules={[
                                                { type: "maxLength", value: 64 },
                                            ]}
                                        />
                                    </Stack>
                                </Row>
                                <hr />

                                <Row className="input-row-area align-top spacing-16px">
                                    <Stack className="input-label-area stack flex-center spacing-4px">
                                        <Row className="flex-left align-center spacing-4px">
                                            <Text className="input-label input-label-text text-size-medium text-bold">メールアドレス</Text>
                                            <Text className="input-required-mark">※必須</Text>
                                        </Row>
                                    </Stack>
                                    <Stack className="input-form-area stack flex-center">
                                        <TextInput
                                            disabled={isUpdateLocked}
                                            type="email"
                                            className="width-100percent"
                                            default={mailAddress}
                                            onchange={(e) => setMailAddress(e.target.value)}
                                            placeholder="メールアドレスを入力"
                                            isLoading={(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete) ? true : false}
                                            rules={[
                                                { type: "required"},
                                                { type: "minLength", value: 1},
                                                { type: "custom", validate: ((v) => {return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)}), message: "メールアドレスを正しく入力してください。"},
                                            ]}
                                        />
                                    </Stack>
                                </Row>
                                <hr />

                                <Row className="input-row-area align-top spacing-16px">
                                    <Stack className="input-label-area stack flex-center spacing-4px">
                                        <Row className="flex-left align-center spacing-4px">
                                            <Text className="input-label input-label-text text-size-medium text-bold">メール件名</Text>
                                            <Text className="input-required-mark">※必須</Text>
                                        </Row>
                                    </Stack>
                                    <Stack className="input-form-area stack flex-center">
                                        <TextInput
                                            disabled={isUpdateLocked}
                                            className="width-100percent"
                                            default={mailSubject}
                                            onchange={(e) => setMailSubject(e.target.value)}
                                            placeholder="メール件名を入力"
                                            isLoading={(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete) ? true : false}
                                            rules={[
                                                { type: "required"},
                                                { type: "minLength", value: 1},
                                            ]}
                                        />
                                    </Stack>
                                </Row>
                                <hr />

                                <Row className="input-row-area align-top spacing-16px">
                                    <Stack className="input-label-area stack flex-center spacing-4px">
                                        <Row className="flex-left align-center spacing-4px">
                                            <Text className="input-label input-label-text text-size-medium text-bold">フォーム説明文</Text>
                                        </Row>
                                        <Text className="input-label-hint text-size-small">未記入の場合は表示されません<br />複数行入力可</Text>
                                    </Stack>
                                    <Stack className="input-form-area stack flex-center">
                                        <FlexibleTextInput
                                            disabled={isUpdateLocked}
                                            className="textinput width-100percent"
                                            default={formDescription}
                                            onchange={(e) => setFormDescription(e)}
                                            placeholder="フォームの説明文を入力"
                                            isLoading={(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete) ? true : false}
                                        />
                                    </Stack>
                                </Row>
                                <hr />

                                <Row className="input-row-area align-top spacing-16px">
                                    <Stack className="input-label-area stack flex-center spacing-4px">
                                        <Row className="flex-left align-center spacing-4px">
                                            <Text className="input-label input-label-text text-size-medium text-bold">プライバシーポリシー</Text>
                                        </Row>
                                        <Text className="input-label-hint text-size-small">未記入の場合は表示されません</Text>
                                    </Stack>
                                    <Stack className="input-form-area stack flex-center">
                                        <TextInput
                                            disabled={isUpdateLocked}
                                            className="width-100percent"
                                            default={privacyPolicyUrl}
                                            onchange={(e) => setPrivacyPolicyUrl(e.target.value)}
                                            placeholder="プライバシーポリシーURLを入力"
                                            isLoading={(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete) ? true : false}
                                        />
                                    </Stack>
                                </Row>
                                <hr />

                                <Row className="input-row-area align-top spacing-16px">
                                    <Stack className="input-label-area stack flex-center spacing-4px">
                                        <Row className="flex-left align-center spacing-4px">
                                            <Text className="input-label input-label-text text-size-medium text-bold">サンクスページ</Text>
                                        </Row>
                                    </Stack>
                                    <Stack className="input-form-area stack flex-center">
                                        <TextSwitch
                                            disabled={isUpdateLocked}
                                            className="redirect-switch"
                                            options={
                                                [
                                                    { value: "popup", label: "ポップアップ"},
                                                    { value: "newPage", label: "別ページ"},
                                                ]
                                            }
                                            onChange={(value) => {setThanksPageMode(value)}}
                                            value={thanksPageMode}
                                        />
                                    </Stack>
                                </Row>

                                <Row className={"input-row-area align-top spacing-16px" + (thanksPageMode === "newPage" ? " hidden" : "")}>
                                    <Stack className="input-label-area stack flex-center spacing-4px">
                                        <Row className="flex-left align-center spacing-4px">
                                            <Text className="input-label input-label-text text-size-medium text-bold">見出し</Text>
                                            <Text className="input-required-mark">※必須</Text>
                                        </Row>
                                    </Stack>
                                    <Stack className="input-form-area stack flex-center">
                                        <TextInput
                                            disabled={isUpdateLocked}
                                            className="width-100percent"
                                            default={thanksHeading}
                                            onchange={(e) => setThanksHeading(e.target.value)}
                                            placeholder="見出しを入力"
                                            isLoading={(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete) ? true : false}
                                            rules={[
                                                { type: "required"},
                                                { type: "minLength", value: 1},
                                            ]}
                                        />
                                    </Stack>
                                </Row>

                                <Row className={"input-row-area align-top spacing-16px" + (thanksPageMode === "newPage" ? " hidden" : "")}>
                                    <Stack className="input-label-area stack flex-center spacing-4px">
                                        <Row className="flex-left align-center spacing-4px">
                                            <Text className="input-label input-label-text text-size-medium text-bold">本文</Text>
                                            <Text className="input-required-mark">※必須</Text>
                                        </Row>
                                        <Text className="input-label-hint text-size-small">複数行入力可</Text>
                                    </Stack>
                                    <Stack className="input-form-area stack flex-center">
                                        <FlexibleTextInput
                                            disabled={isUpdateLocked}
                                            className="textinput width-100percent"
                                            default={thanksText}
                                            onchange={(e) => setThanksText(e)}
                                            placeholder="本文を入力 (改行可)"
                                            isLoading={(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete) ? true : false}
                                            rules={[
                                                { type: "required"},
                                                { type: "minLength", value: 1},
                                            ]}
                                            minHeight={60}
                                        />
                                    </Stack>
                                </Row>
                                <hr className={"input-hr" + (thanksPageMode === "newPage" ? " hidden" : "")} />

                                <Row className={"input-row-area align-top spacing-16px" + (thanksPageMode === "newPage" ? "" : " hidden")}>
                                    <Stack className="input-label-area stack flex-center spacing-4px">
                                        <Row className="flex-left align-center spacing-4px">
                                            <Text className="input-label input-label-text text-size-medium text-bold">URL</Text>
                                            <Text className="input-required-mark">※必須</Text>
                                        </Row>
                                    </Stack>
                                    <Stack className="input-form-area stack flex-center">
                                        <TextInput
                                            disabled={isUpdateLocked}
                                            className="width-100percent"
                                            default={thanksUrl}
                                            onchange={(e) => setThanksUrl(e.target.value)}
                                            placeholder="URLを入力"
                                            isLoading={(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete) ? true : false}
                                            rules={[
                                                { type: "required"},
                                                { type: "minLength", value: 1},
                                            ]}
                                        />
                                    </Stack>
                                </Row>
                                <hr className={"input-hr" + (thanksPageMode === "newPage" ? "" : " hidden")} />

                                <Stack className="summary-format-area width-full spacing-16px">
                                    <Row className="flex-both align-center width-full">
                                        <Stack className="stack flex-center spacing-4px">
                                            <Text className="input-label input-label-text text-size-medium text-bold">要約フォーマット</Text>
                                            <Text className="input-label input-label-text text-size-small">対話ログから要約する際のフォーマットを設定します。</Text>
                                        </Stack>
                                        <Stack>
                                            <Button className="primary-border padding-16px" onClick={handleDownloadTemplate}>
                                                <Row className="flex-center spacing-16px">
                                                    <Icon icon="box-arrow-down" size={24} />
                                                    <div>テンプレートダウンロード</div>
                                                </Row>
                                            </Button>
                                        </Stack>
                                    </Row>

                                    {summaryFormatTAsset &&
                                        <Card className="border-card file-display-area width-full">
                                            <Row className="flex-both align-center spacing-8px width-full">
                                                <Stack className="flex-center align-center">
                                                    {summaryFormatTAsset && summaryFormatTAsset.path}
                                                </Stack>
                                                <Row className="operate-area flex-right spacing-8px align-center">
                                                    <Row className="flex-center align-center spacing-8px width-full">
                                                        <Icon className="time-icon text-color-weak" icon="arrow-clockwise" size={24} />
                                                        <Text>{summaryFormatTAsset && FormatDateTime(summaryFormatTAsset.regdate)}</Text>
                                                    </Row>
                                                    <Button className={"operate-button secondary-border success"} onClick={handleDownloadSummaryFormat}>
                                                        <Row className="text-size-normal text-normal-weight operate-label width-fit">ダウンロード</Row>
                                                    </Button>
                                                </Row>
                                            </Row>
                                        </Card>
                                    }

                                    <Uploader
                                        disabled={isUpdateLocked}
                                        className="custom-uploader width-full"
                                        accept={[".txt", ".md"]}
                                        onFileChanged={(files) => setSummaryFormatAsset(files)}
                                        files={summaryFormatAsset}
                                        isSwap={true}
                                        minimum={false}
                                    />
                                </Stack>
                                <hr />

                                <Stack className="form-items-area width-full spacing-16px">
                                    <Stack className="stack flex-left spacing-4px">
                                        <Text className="input-label input-label-text text-size-medium text-bold">フォーム項目定義</Text>
                                        <Text className="input-label input-label-text text-size-small">問い合わせフォームに表示する項目を設定します。</Text>
                                        {
                                            isFormError &&
                                            <Text className="input-label input-label-text text-size-small text-error">管理名を設定してください。</Text>
                                        }
                                    </Stack>

                                    <Row className="form-item-row form-item-row-header align-center spacing-8px">
                                        {/* ハンドル列のスペーサー: 追加行の列幅をリスト行と揃えるため */}
                                        {! isUpdateLocked &&
                                        <div className="form-item-col-handle" />
                                        }
                                        <div className="form-item-col-required">
                                            <Select
                                                disabled={isUpdateLocked}
                                                title={{ "必須": "true", "任意": "false" }}
                                                value={newItemRequired}
                                                onchange={(val) => setNewItemRequired(val)}
                                            />
                                        </div>
                                        {
                                            isWebhookEnabled &&
                                            <div className="form-item-col-name">
                                                <TextInput
                                                    disabled={isUpdateLocked}
                                                    key={`name-${resetKey}`}
                                                    className="width-100percent"
                                                    default={newItemName}
                                                    placeholder="管理名(アルファベットのみ)"
                                                    restrict={/[^A-Za-z]/g}
                                                    onchange={(e) => setNewItemName(e.target.value)}
                                                />
                                            </div>
                                        }
                                        <div className="form-item-col-label">
                                            <TextInput
                                                disabled={isUpdateLocked}
                                                key={`label-${resetKey}`}
                                                className="width-100percent"
                                                default={newItemLabel}
                                                placeholder="見出し"
                                                onchange={(e) => setNewItemLabel(e.target.value)}
                                            />
                                        </div>
                                        <div className="form-item-col-type">
                                            <Select
                                                disabled={isUpdateLocked}
                                                title={getTypeOptions()}
                                                value={newItemType}
                                                onchange={handleTypeChange}
                                            />
                                        </div>
                                        <div className="form-item-col-placeholder">
                                            <TextInput
                                                disabled={isUpdateLocked}
                                                key={`placeholder-${resetKey}`}
                                                className="width-100percent"
                                                default={newItemPlaceholder}
                                                placeholder="プレースホルダ"
                                                onchange={(e) => setNewItemPlaceholder(e.target.value)}
                                            />
                                        </div>
                                        <div className="form-item-col-action">
                                            <Button className={"form-item-action-button add" + (isUpdateLocked ? " disabled" : "")} disabled={isUpdateLocked} onClick={handleAddItem}>
                                                <Icon icon="plus" size={36} />
                                            </Button>
                                        </div>
                                    </Row>

                                    <Stack className="spacing-8px">
                                        {formItems.map((item, index) => {
                                            const isEditing = editingIndex === index;
                                            return (
                                            // Row の代わりに div を使用: Row は draggable などのDOMプロパティを渡せないため
                                            <div
                                                key={index}
                                                className={"row-stack form-item-row align-center spacing-8px"
                                                    + (dragItemIndex === index ? " dragging" : "")
                                                    + (dragOverIndex === index && dragOverIndex !== dragItemIndex ? " drag-over" : "")
                                                    + (isEditing ? " editing" : "")}
                                                draggable={!isEditing && !isUpdateLocked}
                                                onDragStart={() => handleDragStart(index)}
                                                onDragOver={(e) => handleDragOver(e, index)}
                                                onDrop={(e) => handleDrop(e, index)}
                                                onDragEnd={handleDragEnd}
                                                onDoubleClick={() => { if (!isEditing && !isUpdateLocked) startEditItem(index); }}
                                                title={(isEditing || isUpdateLocked) ? "" : "ダブルクリックで編集"}
                                            >
                                                {/* ドラッグハンドル: 摘みとして機能するアイコン。並べ替えできない場合は列ごと出さない（追加行のスペーサーも同条件） */}
                                                {! isUpdateLocked &&
                                                <div className="form-item-col-handle">
                                                    <Icon icon="grip-vertical" size={18} className="drag-handle-icon" />
                                                </div>
                                                }
                                                {isEditing ? (
                                                    <>
                                                        <div className="form-item-col-required">
                                                            <Select
                                                                disabled={isUpdateLocked}
                                                                title={{ "必須": "true", "任意": "false" }}
                                                                value={editingRequired}
                                                                onchange={(val) => setEditingRequired(val)}
                                                            />
                                                        </div>
                                                        {
                                                            isWebhookEnabled &&
                                                            <div className="form-item-col-name">
                                                                <TextInput
                                                                    disabled={isUpdateLocked}
                                                                    className="width-100percent"
                                                                    default={editingName}
                                                                    placeholder="管理名(アルファベットのみ)"
                                                                    restrict={/[^A-Za-z]/g}
                                                                    onchange={(e) => setEditingName(e.target.value)}
                                                                />
                                                            </div>
                                                        }
                                                        <div className="form-item-col-label">
                                                            <TextInput
                                                                disabled={isUpdateLocked}
                                                                className="width-100percent"
                                                                default={editingLabel}
                                                                placeholder="見出し"
                                                                onchange={(e) => setEditingLabel(e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="form-item-col-type">
                                                            <Select
                                                                disabled={isUpdateLocked}
                                                                title={getTypeOptions()}
                                                                value={editingType}
                                                                onchange={(val) => setEditingType(val)}
                                                            />
                                                        </div>
                                                        <div className="form-item-col-placeholder">
                                                            <TextInput
                                                                disabled={isUpdateLocked}
                                                                className="width-100percent"
                                                                default={editingPlaceholder}
                                                                placeholder="プレースホルダ"
                                                                onchange={(e) => setEditingPlaceholder(e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="form-item-col-action">
                                                            <Row className="flex-center spacing-4px">
                                                                <Button className={"form-item-action-button save" + (isUpdateLocked ? " disabled" : "")} disabled={isUpdateLocked} onClick={saveEditItem} title="保存">
                                                                    <Icon icon="check-lg" size={18} />
                                                                </Button>
                                                                <Button className="form-item-action-button cancel" onClick={cancelEditItem} title="キャンセル">
                                                                    <Icon icon="x-lg" size={18} />
                                                                </Button>
                                                            </Row>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="form-item-col-required">
                                                            <div className={"form-item-badge " + (item.required ? "required" : "optional")}>
                                                                {item.required ? "必須" : "任意"}
                                                            </div>
                                                        </div>
                                                        {
                                                            isWebhookEnabled &&
                                                            <div className="form-item-col-name">
                                                                <Text className={"text-size-normal" + ((isFormError && !item.name) ? " form-error" : "")}>{item.name || ""}</Text>
                                                            </div>
                                                        }
                                                        <div className="form-item-col-label">
                                                            <Text className="text-size-normal">{item.label}</Text>
                                                        </div>
                                                        <div className="form-item-col-type">
                                                            <Text className="text-size-normal">{getTypeLabel(item.type)}</Text>
                                                        </div>
                                                        <div className="form-item-col-placeholder">
                                                            <Text className="text-size-normal text-color-weak">{item.placeholder || ""}</Text>
                                                        </div>
                                                        <div className="form-item-col-action">
                                                            {! isUpdateLocked &&
                                                            <Button className={"form-item-action-button remove"} onClick={() => handleRemoveItem(index)}>
                                                                <Icon icon="x-lg" size={18} />
                                                            </Button>
                                                            }
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                            );
                                        })}
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Card>
                    </Stack>
                    <Button className="scroll-button primary" onClick={() => {scrollToRef(topRef)}}>
                        <Icon icon="chevron-up" size={28} className="copy-icon"/>
                    </Button>
                </PageContainer>
            </PageWrapper>
            <CommonModal modalInfo={modalInfo} setModalInfo={setModalInfo}></CommonModal>
        </PageContent>
    );
};

export default AdminUserInquirySetting;
